import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sun, Sunset, Moon, Coffee, Plus, Trash2, Sparkles, Clock, CopyPlus, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { ExportButton } from "../components/ExportButton";

const MGR = ["super_admin", "admin", "responsabile", "segreteria"];
const TURNI = [
  { key: "mattina", label: "Turno Mattina", icon: Sun, color: "#d97706", ring: "ring-amber-300" },
  { key: "pomeriggio", label: "Turno Pomeriggio", icon: Sunset, color: "#ea580c", ring: "ring-orange-300" },
  { key: "notte", label: "Turno Notte", icon: Moon, color: "#4f46e5", ring: "ring-indigo-300" },
];

const isNowItem = (turno, oi, of) => {
  if (!oi || !of) return false;
  const toMin = (s) => { const p = (s || "").split(":"); return (parseInt(p[0]) || 0) * 60 + (parseInt(p[1]) || 0); };
  let a = toMin(oi), b = toMin(of);
  const d = new Date(); let now = d.getHours() * 60 + d.getMinutes();
  if (turno === "notte") { if (a < 720) a += 1440; if (b <= 720) b += 1440; if (now < 720) now += 1440; }
  if (b <= a) return false;
  return now >= a && now < b;
};

export default function PianoLavoro() {
  const { selectedStructure, structures, user } = useAuth();
  const qc = useQueryClient();
  const isMgr = MGR.includes(user?.role);
  const single = selectedStructure && selectedStructure !== "all";
  const [form, setForm] = useState({});
  const h = new Date().getHours();
  const curTurno = h >= 6 && h < 14 ? "mattina" : h >= 14 && h < 20 ? "pomeriggio" : "notte";
  const [copyFrom, setCopyFrom] = useState("");

  const { data } = useQuery({
    enabled: !!single,
    queryKey: ["piano-lavoro", selectedStructure],
    queryFn: async () => (await api.get("/piano-lavoro", { params: { structure_id: selectedStructure } })).data,
  });

  // Orari EFFETTIVI (per confronto con il piano)
  const _t = new Date();
  const todayIso = `${_t.getFullYear()}-${String(_t.getMonth() + 1).padStart(2, "0")}-${String(_t.getDate()).padStart(2, "0")}`;
  const sp = { structure_id: selectedStructure };
  const { data: pvDett } = useQuery({ enabled: !!single, refetchInterval: 30000, queryKey: ["piano-pv-consegne", selectedStructure],
    queryFn: async () => { try { return (await api.get("/dashboard/presa-visione/dettaglio", { params: { ...sp, tipo: "consegne" } })).data; } catch { return null; } } });
  const { data: alzateData } = useQuery({ enabled: !!single, refetchInterval: 30000, queryKey: ["piano-alzate", selectedStructure],
    queryFn: async () => { try { return (await api.get("/alzate", { params: { ...sp, tipo: "alzate", data: todayIso } })).data; } catch { return null; } } });
  const { data: messaData } = useQuery({ enabled: !!single, refetchInterval: 30000, queryKey: ["piano-messa", selectedStructure],
    queryFn: async () => { try { return (await api.get("/alzate", { params: { ...sp, tipo: "messa_a_letto", data: todayIso } })).data; } catch { return null; } } });

  const hm = (ts) => { try { return new Date(ts).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); } catch { return null; } };
  // Consegne: orario in cui TUTTI gli operatori del turno hanno preso visione (100% copertura)
  const consegneEff = {};
  (pvDett?.turni || []).forEach((tb) => {
    const conf = tb.confirmati || [];
    if (conf.length === 0) { consegneEff[tb.turno] = null; return; }
    const last = conf.map((c) => c.ts).filter(Boolean).sort().slice(-1)[0];
    const completo = (tb.mancanti || []).length === 0;
    consegneEff[tb.turno] = { ora: hm(last), completo, conf: conf.length, attesi: (tb.attesi || []).length };
  });
  const alzateEff = alzateData?.completato_ora || null;
  const messaEff = messaData?.completato_ora || null;

  const matchTipo = (attivita) => {
    const s = (attivita || "").toLowerCase();
    if (s.includes("consegn")) return "consegne";
    if (s.includes("letto")) return "messa_a_letto";
    if (s.includes("alzat")) return "alzate";
    return null;
  };
  const toMin = (s) => { const p = (s || "").split(":"); return (parseInt(p[0]) || 0) * 60 + (parseInt(p[1]) || 0); };
  const EffettivoBadge = ({ tipo, turno, oraInizio }) => {
    let ora = null, extra = null;
    if (tipo === "consegne") { const c = consegneEff[turno]; if (c) { ora = c.ora; if (!c.completo) extra = `${c.conf}/${c.attesi}`; } }
    else if (tipo === "alzate") ora = alzateEff;
    else if (tipo === "messa_a_letto") ora = messaEff;
    if (!ora) return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground bg-muted rounded px-1.5 py-0.5 ml-1.5" data-testid={`piano-eff-${tipo}-${turno}`}><Clock size={10} /> effettivo: —</span>;
    const diff = oraInizio ? toMin(ora) - toMin(oraInizio) : null;
    const late = diff != null && diff > 15;
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold rounded px-1.5 py-0.5 ml-1.5 ${late ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`} data-testid={`piano-eff-${tipo}-${turno}`}>
        {late ? <AlertTriangle size={10} /> : <CheckCircle2 size={10} />} effettivo: {ora}{extra ? ` (${extra})` : ""}{diff != null && diff > 0 ? ` · +${diff}′` : ""}
      </span>
    );
  };

  const setF = (turno, k, v) => setForm((p) => ({ ...p, [turno]: { ...(p[turno] || {}), [k]: v } }));
  const add = async (turno) => {
    const f = form[turno] || {};
    if (!f.attivita || !f.attivita.trim()) { toast.error("Inserisci l'attività"); return; }
    try {
      await api.post("/piano-lavoro", { structure_id: selectedStructure, turno, ora_inizio: f.ora_inizio || "", ora_fine: f.ora_fine || "", attivita: f.attivita.trim(), is_pausa: !!f.is_pausa });
      setForm((p) => ({ ...p, [turno]: {} }));
      qc.invalidateQueries({ queryKey: ["piano-lavoro"] });
      toast.success(f.is_pausa ? "Pausa aggiunta" : "Mansione aggiunta");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (id) => {
    try { await api.delete(`/piano-lavoro/${id}`); qc.invalidateQueries({ queryKey: ["piano-lavoro"] }); toast.success("Rimosso"); }
    catch { toast.error("Errore"); }
  };
  const seed = async () => {
    try { const r = await api.post("/piano-lavoro/seed", { structure_id: selectedStructure }); qc.invalidateQueries({ queryKey: ["piano-lavoro"] }); toast.success(r.data.created ? `Piano predefinito creato (${r.data.created} voci)` : "Piano già presente"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const doCopy = async () => {
    if (!copyFrom) { toast.error("Scegli la struttura di origine"); return; }
    try {
      let r = await api.post("/piano-lavoro/copy", { source_structure_id: copyFrom, target_structure_id: selectedStructure });
      if (r.data.needs_confirm) {
        if (!window.confirm(`Questa struttura ha già ${r.data.existing} voci nel piano. Sovrascriverle con il piano copiato?`)) return;
        r = await api.post("/piano-lavoro/copy", { source_structure_id: copyFrom, target_structure_id: selectedStructure, overwrite: true });
      }
      if (r.data.ok) { qc.invalidateQueries({ queryKey: ["piano-lavoro"] }); toast.success(`Copiate ${r.data.copied} voci`); setCopyFrom(""); }
      else toast.error(r.data.message || "Nessuna voce copiata");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const structName = single ? ((structures || []).find((s) => s.id === selectedStructure)?.nome || "") : "";
  const structColor = single ? ((structures || []).find((s) => s.id === selectedStructure)?.colore || "#1f7a5c") : "#1f7a5c";
  const exportRows = TURNI.flatMap((t) => (data?.[t.key] || []).map((it) => ({ turno: t.label, orario: `${it.ora_inizio || ""}${it.ora_fine ? " – " + it.ora_fine : ""}`, attivita: (it.is_pausa ? "PAUSA · " : "") + it.attivita })));
  const exportCols = [{ header: "Turno", accessor: "turno" }, { header: "Orario", accessor: "orario" }, { header: "Attività", accessor: "attivita" }];
  const totale = TURNI.reduce((n, t) => n + (data?.[t.key]?.length || 0), 0);
  const otherStructs = (structures || []).filter((s) => s.id !== selectedStructure);

  return (
    <div>
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><Clock size={22} className="text-primary" /> Piano di Lavoro OSS</h1>
          <p className="text-sm text-muted-foreground">La road map giornaliera: cosa fare e a che ora, divisa per turno. Le pause sono evidenziate.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportButton filename="piano-lavoro-oss" title="Piano di Lavoro OSS" subtitle={structName} columns={exportCols} rows={exportRows} color={structColor} testid="piano-export" />
          {isMgr && single && otherStructs.length > 0 && (
            <div className="flex items-center gap-1.5" data-testid="piano-copy">
              <select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)} className="h-9 rounded-md border px-2 text-sm" data-testid="piano-copy-from">
                <option value="">Copia da…</option>
                {otherStructs.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
              <Button size="sm" variant="outline" onClick={doCopy} disabled={!copyFrom} data-testid="piano-copy-btn"><CopyPlus size={15} className="mr-1" /> Copia qui</Button>
            </div>
          )}
          {isMgr && single && totale === 0 && <Button onClick={seed} data-testid="piano-seed" className="font-semibold"><Sparkles size={16} className="mr-1" /> Popola piano predefinito</Button>}
        </div>
      </div>

      {!single && <div className="gr-card p-8 text-center text-muted-foreground" data-testid="piano-no-struct">Seleziona una struttura specifica in alto per vedere e modificare il piano di lavoro.</div>}

      {single && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start" data-testid="piano-cols">
          {TURNI.map((t) => {
            const items = data?.[t.key] || [];
            const f = form[t.key] || {};
            const isCur = t.key === curTurno;
            return (
              <div key={t.key} className={`gr-card p-4 ring-1 ${isCur ? "ring-primary" : t.ring}`} data-testid={`piano-turno-${t.key}`}>
                <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                  <t.icon size={18} style={{ color: t.color }} />
                  <h2 className="font-head font-bold" style={{ color: t.color }}>{t.label.replace("Turno ", "")}</h2>
                  {isCur && <span className="text-[9px] uppercase font-extrabold text-primary">• ora</span>}
                  <span className="ml-auto text-xs font-semibold text-muted-foreground tabnum">{items.length} voci</span>
                </div>

                {items.length === 0 && <p className="text-sm text-muted-foreground py-2" data-testid={`piano-empty-${t.key}`}>Nessuna mansione. {isMgr ? "Aggiungine una qui sotto." : ""}</p>}

                <div className="space-y-1.5">
                  {items.map((it) => { const now = isNowItem(t.key, it.ora_inizio, it.ora_fine); return (
                    <div key={it.id} data-testid={`piano-item-${it.id}`}
                      className={`flex items-start gap-2 rounded-lg px-2.5 py-2 border ${now ? "ring-2 ring-primary border-primary/40 bg-primary/5" : it.is_pausa ? "bg-emerald-50 border-emerald-200" : "bg-secondary/40 border-transparent"}`}>
                      <div className="tabnum text-xs font-bold text-primary shrink-0 w-[6.2rem] pt-0.5">
                        {it.ora_inizio || "—"}{it.ora_fine ? <span className="text-muted-foreground font-medium"> – {it.ora_fine}</span> : ""}
                      </div>
                      <div className="min-w-0 flex-1 text-sm">
                        {now && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-white bg-primary rounded px-1.5 py-0.5 mr-1.5" data-testid={`piano-now-${it.id}`}>Adesso</span>}
                        {it.is_pausa && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5 mr-1.5" data-testid={`piano-pausa-${it.id}`}><Coffee size={10} /> Pausa</span>}
                        <span className={it.is_pausa ? "font-semibold text-emerald-800" : "font-medium"}>{it.attivita}</span>
                        {matchTipo(it.attivita) && !it.is_pausa && <EffettivoBadge tipo={matchTipo(it.attivita)} turno={t.key} oraInizio={it.ora_inizio} />}
                      </div>
                      {isMgr && <button onClick={() => del(it.id)} data-testid={`piano-del-${it.id}`} className="text-muted-foreground hover:text-destructive shrink-0 mt-0.5"><Trash2 size={15} /></button>}
                    </div>
                  ); })}
                </div>

                {isMgr && (
                  <div className="mt-3 pt-3 border-t space-y-2" data-testid={`piano-add-${t.key}`}>
                    <div className="flex items-center gap-2">
                      <Input type="time" value={f.ora_inizio || ""} onChange={(e) => setF(t.key, "ora_inizio", e.target.value)} className="w-28" data-testid={`piano-inizio-${t.key}`} />
                      <span className="text-muted-foreground text-sm">–</span>
                      <Input type="time" value={f.ora_fine || ""} onChange={(e) => setF(t.key, "ora_fine", e.target.value)} className="w-28" data-testid={`piano-fine-${t.key}`} />
                    </div>
                    <Input placeholder={f.is_pausa ? "Es. Pausa pranzo" : "Es. Alzata e igiene"} value={f.attivita || ""}
                      onChange={(e) => setF(t.key, "attivita", e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") add(t.key); }}
                      data-testid={`piano-attivita-${t.key}`} />
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={!!f.is_pausa} onCheckedChange={(v) => setF(t.key, "is_pausa", !!v)} data-testid={`piano-ispausa-${t.key}`} />
                        <span className="flex items-center gap-1"><Coffee size={13} /> È una pausa</span>
                      </label>
                      <Button size="sm" onClick={() => add(t.key)} data-testid={`piano-addbtn-${t.key}`} className="font-semibold"><Plus size={15} className="mr-1" /> Aggiungi</Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </>
      )}
    </div>
  );
}
